import * as React from 'react';
import Typography from '@mui/material/Typography';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
} from '@mui/material';
import colors from 'assets/theme/base/colors';
import pxToRem from 'assets/theme/function/px-to-rem';
import borders from 'assets/theme/base/borders';
import { StyledTableRow } from 'component/tabel-pagination-style';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { RowDataType, TableMetaData } from 'types/common/common';
import { initialListData } from 'pages/card';
import { useUsersCardDetailsQuery } from 'store/slices/user/user';

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'sphereTitle',
        position: 1,
        title: 'Sphere',
        isSortable: false,
    },
    {
        key: 'personageTitle',
        position: 2,
        title: 'Personage',
        isSortable: false,
    },
    {
        key: 'album',
        position: 3,
        title: 'Album',
        isSortable: false,
    },
    {
        key: 'cardCount',
        position: 4,
        title: 'Card Count',
        isSortable: false,
    },
    {
        key: 'createdDate',
        position: 5,
        title: 'Creation Date',
        isSortable: false,
    },
];

interface Props {
    userId: string;
}

const UserCardInfo: React.FC<Props> = ({ userId }) => {
    const [tableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [pagesCount, setPagesCount] = useState<number>(0);
    const [page, setPage] = useState<number>(initialListData.page);
    const [pageSize, setPageSize] = useState<number>(initialListData.pageSize);
    const [tableData, setTableData] = useState<RowDataType[]>([]);

    const { data: userCardInfo } = useUsersCardDetailsQuery({
        userId: userId ?? '',
    });

    const handleFilterData = (currentPage = 0) => {
        setPage(currentPage + 1);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    useEffect(() => {
        setPagesCount(
            userCardInfo?.data && userCardInfo?.data?.length > 0
                ? Number(userCardInfo.rowsCount)
                : 0,
        );
        const tableData: RowDataType[] = userCardInfo
            ? userCardInfo.data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [userCardInfo]);

    return (
        <Box
            bgcolor={colors.secondary.light}
            borderRadius={borders.borderRadius.md}
            mt={pxToRem(7)}
        >
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                p={1}
            >
                <Typography variant="h6" pl={pxToRem(10)}>
                    User Card Info
                </Typography>
            </Box>

            <TableContainer
                sx={{
                    minHeight: pxToRem(150),
                    maxHeight: pxToRem(300),
                    overflowY: 'auto',
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            {tableMetaDataList.map((col) => (
                                <TableCell key={col.key}>{col.title}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tableData.map((row, idx) => (
                            <StyledTableRow key={idx} data={row}>
                                {tableMetaDataList.map((col) => (
                                    <TableCell key={col.key}>
                                        {col.key === 'createdDate' &&
                                        row[col.key]
                                            ? dayjs(row[col.key]).format(
                                                  'DD.MM.YYYY',
                                              )
                                            : row[col.key]}
                                    </TableCell>
                                ))}
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {pagesCount > 0 && (
                <Stack spacing={2} alignItems={'end'}>
                    <TablePagination
                        rowsPerPageOptions={[10, 30, 50]}
                        component="div"
                        count={pagesCount}
                        rowsPerPage={pageSize}
                        page={page - 1}
                        labelRowsPerPage={'Number of rows'}
                        slotProps={{
                            select: {
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                MenuProps: {
                                    disablePortal: true,
                                    slotProps: {
                                        paper: {
                                            sx: {
                                                backgroundColor:
                                                    colors.secondary.main,
                                                padding: 0,
                                            },
                                        },
                                    },
                                },
                                native: false,
                            },
                        }}
                        onPageChange={(_, page) => handleFilterData(page)}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Stack>
            )}
        </Box>
    );
};

export default UserCardInfo;
